import { saveAs } from "file-saver";
import {
  Document, Packer, Paragraph, TextRun, Table, TableRow, TableCell,
  AlignmentType, BorderStyle, WidthType, ShadingType, HeadingLevel,
} from "docx";

const border    = { style: BorderStyle.SINGLE, size: 1, color: "CCCCCC" };
const borders   = { top: border, bottom: border, left: border, right: border };

const makeCell = (text, widthDxa, opts = {}) =>
  new TableCell({
    borders,
    width: { size: widthDxa, type: WidthType.DXA },
    margins: { top: 80, bottom: 80, left: 120, right: 120 },
    shading: opts.shade ? { fill: opts.shade, type: ShadingType.CLEAR } : undefined,
    children: [
      new Paragraph({
        children: [
          new TextRun({
            text:  String(text || "—"),
            bold:  opts.bold  || false,
            size:  opts.size  || 18,
            color: opts.color || "000000",
            font:  "Arial",
          }),
        ],
      }),
    ],
  });

const responseLabel = (val) => {
  if (!val) return "—";
  return { yes: "Yes", no: "No", partial: "Partial" }[val.toLowerCase()] || val;
};

const responseColor = (val) => {
  if (!val) return "999999";
  return { yes: "16a34a", no: "dc2626", partial: "d97706" }[val.toLowerCase()] || "000000";
};

export const downloadFormDocx = async (record) => {
  try {
    const res = await fetch(`${process.env.REACT_APP_API_URL}/form/${record.id}`);
    if (!res.ok) throw new Error(`API error: ${res.status}`);
    const data = await res.json();

    const rawSections = (data.structure?.sections || []).sort((a, b) => a.order - b.order);
    const tool     = data.structure?.tool      || "";
    const toolType = data.structure?.tool_type || "";

    const children = [];

    // ── Title ──
    children.push(
      new Paragraph({
        heading: HeadingLevel.HEADING_1,
        children: [new TextRun({ text: "Maturity Assessment Report", bold: true, size: 40, font: "Arial", color: "bb1ccc" })],
      }),
      new Paragraph({
        spacing: { after: 120 },
        children: [
          new TextRun({ text: `Tool Type: `, size: 20, font: "Arial", color: "555555" }),
          new TextRun({ text: toolType, size: 20, font: "Arial", bold: true }),
          new TextRun({ text: `   Tool Name: `, size: 20, font: "Arial", color: "555555" }),
          new TextRun({ text: tool, size: 20, font: "Arial", bold: true }),
          new TextRun({ text: `   Submitted By: `, size: 20, font: "Arial", color: "555555" }),
          new TextRun({ text: record.submittedBy || "", size: 20, font: "Arial", bold: true }),
        ],
      }),
      new Paragraph({ children: [new TextRun({ text: "" })] }) // spacer
    );

    const COL_Q    = 4200;
    const COL_DESC = 3000;
    const COL_SUB  = 2200;
    const COL_RESP = 1000;
    const COL_CMT  = 1800;
    const TOTAL    = COL_Q + COL_DESC + COL_SUB + COL_RESP + COL_CMT; // 12200

    // ── Sections ──
    for (const sec of rawSections) {
      children.push(
        new Paragraph({
          heading: HeadingLevel.HEADING_2,
          spacing: { before: 280, after: 140 },
          children: [new TextRun({ text: sec.section, bold: true, size: 26, font: "Arial", color: "1e293b" })],
        })
      );

      const headerRow = new TableRow({
        tableHeader: true,
        children: [
          makeCell("Question",      COL_Q,    { bold: true, shade: "E8D5F5", size: 18 }),
          makeCell("Description",   COL_DESC, { bold: true, shade: "E8D5F5", size: 18 }),
          makeCell("Sub Dimension", COL_SUB,  { bold: true, shade: "E8D5F5", size: 18 }),
          makeCell("Response",      COL_RESP, { bold: true, shade: "E8D5F5", size: 18 }),
          makeCell("Comment",       COL_CMT,  { bold: true, shade: "E8D5F5", size: 18 }),
        ],
      });

      const dataRows = sec.questions.map((q, i) => {
        const shade = i % 2 === 0 ? "FFFFFF" : "FAFAFA";
        const resp  = responseLabel(q.response);
        return new TableRow({
          children: [
            makeCell(q.question,     COL_Q,    { shade, size: 17 }),
            makeCell(q.description,  COL_DESC, { shade, size: 17, color: "555555" }),
            makeCell(q.subDimension, COL_SUB,  { shade, size: 17, color: "555555" }),
            makeCell(resp,           COL_RESP, { shade, size: 17, bold: true, color: responseColor(q.response) }),
            makeCell(q.comment || "",COL_CMT,  { shade, size: 17, color: "555555" }),
          ],
        });
      });

      children.push(
        new Table({
          width: { size: TOTAL, type: WidthType.DXA },
          columnWidths: [COL_Q, COL_DESC, COL_SUB, COL_RESP, COL_CMT],
          rows: [headerRow, ...dataRows],
        }),
        new Paragraph({ children: [new TextRun({ text: "" })] }) // spacer
      );
    }

    // 2. Build doc
    const doc = new Document({
      styles: {
        default: { document: { run: { font: "Arial", size: 20 } } },
        paragraphStyles: [
          {
            id: "Heading1", name: "Heading 1", basedOn: "Normal", next: "Normal", quickFormat: true,
            run: { size: 40, bold: true, font: "Arial" },
            paragraph: { spacing: { before: 240, after: 240 }, outlineLevel: 0 },
          },
          {
            id: "Heading2", name: "Heading 2", basedOn: "Normal", next: "Normal", quickFormat: true,
            run: { size: 26, bold: true, font: "Arial" },
            paragraph: { spacing: { before: 200, after: 120 }, outlineLevel: 1 },
          },
        ],
      },
      sections: [{
        properties: {
          page: {
            size: { width: 15840, height: 12240 }, // A4 landscape
            margin: { top: 1080, right: 1080, bottom: 1080, left: 1080 },
          },
        },
        children,
      }],
    });

    // 3. Download
    const blob = await Packer.toBlob(doc);
    saveAs(blob, `Assessment_${tool}_${toolType}.docx`);

  } catch (err) {
    console.error("Download failed:", err);
    alert(`Download failed: ${err.message}`);
  }
};